import React from "react";
import styled from "@emotion/styled/macro";
import BadgeWrapper from "./BadgeType";

// Define RewardAmount before using it in Card's styles
const RewardAmount = styled.span`
  color: rgb(33, 37, 67);
  font-size: 36px;
  font-family: Montserrat, sans-serif;
  font-weight: 700;
  text-align: center;
  transition: color 0.2s ease;
`;

const Card = styled.div`
  display: flex;
  justify-content: space-between;
  flex-direction: column;
  align-items: flex-start;
  flex: 1;
  gap: 16px;
  box-shadow: 0px 7px 30px rgba(0, 0, 0, 0.05);
  border-radius: 16px;
  width: 100%;
  background: rgb(255, 255, 255);
  padding: 16px 0px;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  min-height: 304.41px;
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0px 7px 16px rgba(66, 149, 254, 0.15);
    border: solid 1px rgba(66, 149, 254, 0.7);

    ${RewardAmount} {
      color: rgb(97, 98, 197);
    }
  }
`;

const DealCard = ({
  badgeType,
  logoUrl,
  title,
  reward,
  onClick,
  onFavorite,
  isFavorite = false,
  className,
}) => {

  return (
    <Card
      className={className}
      onClick={onClick}
      style={{ cursor: "pointer" }} // makes the whole card feel clickable
    >
      <BadgeWrapper badgeType={badgeType} />
      <ButtonWrapper>
        <FavoriteButton
          onClick={(e) => {
            e.stopPropagation(); // 👈 stops the card click
            onFavorite();
          }}
        >
          {isFavorite ? HeartIconFilled : HeartIconOutlined}
        </FavoriteButton>
      </ButtonWrapper>

      <Logo>
        <LogoImage src={logoUrl} />
      </Logo>

      <RewardSection>
        <TitleWrapper>
          <Title>{title}</Title>
        </TitleWrapper>

        <RewardInfo>
          {reward && (
            <RewardAmount>
              £{Number(reward)?.toLocaleString("en-GB")}
            </RewardAmount>
          )}
          <RewardLabel>Reward</RewardLabel>
        </RewardInfo>
      </RewardSection>
    </Card>
  );
};

const Badge = styled.div`
  position: absolute;
  top: 12px;
  left: -8px;
`;

const TopSection = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: flex-start;
  width: 100%;
  padding: 0 16px;
`;

const Logo = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 0 4px;
  align-self: stretch;
  height: 90px;
  max-height: 90px;
`;

const LogoImage = styled.img`
  max-width: 100%;
  max-height: 90px;
  object-fit: contain;
  display: block;
`;

const RewardSection = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  align-self: stretch;
  padding: 16px;
`;

const TitleWrapper = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  align-self: stretch;
`;

const Title = styled.span`
  color: rgb(100, 116, 139);
  font-size: 14px;
  font-family: Montserrat, sans-serif;
  font-weight: 500;
  line-height: 160%;
  text-align: center;
  min-height: 44px;
`;

const RewardInfo = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: center;
  align-self: stretch;
`;

const RewardLabel = styled.span`
  color: rgb(33, 37, 67);
  font-size: 14px;
  font-family: Montserrat, sans-serif;
  font-weight: 500;
  line-height: 160%;
  text-align: center;
`;

const ButtonWrapper = styled.div`
  padding: 0;
  margin: 0;
  position: absolute;
  right: 20px;
  top: 10px;
  display: flex;
  gap: 2px;
`;
const FavoriteButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
`;

const HeartIconOutlined = (
  <svg
    width="17"
    height="16"
    viewBox="0 0 17 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M7.63086 14.6562L7.56836 14.5625L2.09961 9.5C1.13086 8.59375 0.599609 7.34375 0.599609 6.03125V5.9375C0.599609 3.75 2.16211 1.84375 4.31836 1.4375C5.53711 1.1875 6.78711 1.46875 7.81836 2.1875C8.09961 2.375 8.34961 2.625 8.59961 2.875C8.72461 2.75 8.84961 2.59375 9.00586 2.46875C9.13086 2.375 9.25586 2.28125 9.38086 2.1875C10.3809 1.46875 11.6309 1.1875 12.8496 1.4375C15.0371 1.84375 16.5996 3.75 16.5996 5.9375V6.03125C16.5996 7.34375 16.0371 8.59375 15.0684 9.5L9.59961 14.5625L9.53711 14.6562C9.28711 14.875 8.94336 15.0312 8.59961 15.0312C8.22461 15.0312 7.91211 14.875 7.63086 14.6562ZM8.06836 4.53125C8.03711 4.53125 8.03711 4.53125 8.03711 4.5L7.47461 3.875C6.75586 3.0625 5.66211 2.71875 4.59961 2.90625C3.13086 3.1875 2.09961 4.46875 2.09961 5.9375V6.03125C2.09961 6.9375 2.44336 7.78125 3.09961 8.375L8.59961 13.4688L14.0684 8.375C14.7246 7.78125 15.0996 6.9375 15.0996 6.03125V5.9375C15.0996 4.46875 14.0371 3.1875 12.5684 2.90625C11.5059 2.71875 10.4121 3.0625 9.69336 3.875L9.13086 4.5C9.13086 4.53125 9.13086 4.53125 9.09961 4.5625C8.97461 4.6875 8.78711 4.78125 8.59961 4.78125C8.38086 4.78125 8.19336 4.6875 8.06836 4.5625V4.53125Z"
      fill="#64748B"
    />
  </svg>
);

const HeartIconFilled = (
  <svg
    width="17"
    height="16"
    viewBox="0 0 17 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M1.86816 9.40625C0.930664 8.53125 0.399414 7.28125 0.399414 5.96875V5.8125C0.399414 3.625 1.96191 1.75 4.11816 1.40625C5.55566 1.15625 6.99316 1.625 8.02441 2.625L8.39941 3L8.77441 2.625C9.77441 1.625 11.2432 1.15625 12.6494 1.40625C14.8057 1.75 16.3994 3.625 16.3994 5.8125V5.96875C16.3994 7.28125 15.8369 8.53125 14.8994 9.40625L9.24316 14.6875C9.02441 14.9062 8.71191 15 8.39941 15C8.05566 15 7.74316 14.9062 7.52441 14.6875L1.86816 9.40625Z"
      fill="rgba(253, 77, 147, 1)"
    />
  </svg>
);
export default DealCard;
